import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, ArrowLeft, Package, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import cuttingPlanService from '../services/cuttingPlanService';
import moduleService from '../services/moduleService';
import toast from 'react-hot-toast';
import ModuleCard from './ModuleCard';
import ModuleFormModal from './ModuleFormModal';
import ConfirmModal from './ConfirmModal';

const ProjectModules = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  const [plan, setPlan] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [confirmState, setConfirmState] = useState({ show: false, module: null });

  const canAccess = hasRole?.(['GERENCIA']) || hasRole?.(['RECEPCAO_COMPRAS_AGENDA']);

  const loadData = async () => {
    if (!canAccess) {
      setError('Você não tem permissão para acessar este recurso');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const planResponse = await cuttingPlanService.getPlanById(id);
      if (planResponse.success && planResponse.data) {
        setPlan(planResponse.data);

        const modulesResponse = await moduleService.getModulesByProject(planResponse.data.project_id);
        if (modulesResponse.success && Array.isArray(modulesResponse.data)) {
          setModules(modulesResponse.data);
        } else {
          setModules([]);
        }
      } else {
        setError('Plano de corte não encontrado');
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar dados');
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id, canAccess]);

  const handleCreate = () => {
    setEditingModule(null);
    setShowForm(true);
  };

  const handleEdit = (module) => {
    setEditingModule(module);
    setShowForm(true);
  };

  const handleDelete = async (module) => {
    setConfirmState({ show: true, module });
  };

  const handleConfirmDelete = async () => {
    const module = confirmState.module;
    setConfirmState({ show: false, module: null });

    try {
      await moduleService.deleteModule(module.id);
      toast.success('Módulo excluído com sucesso');
      loadData();
    } catch (err) {
      console.error('Erro ao deletar módulo:', err);
      toast.error('Erro ao deletar módulo');
    }
  };

  const handleViewParts = (module) => {
    navigate(`/modules/${module.id}/parts`);
  };

  const handleSave = async (moduleId, data) => {
    try {
      if (moduleId) {
        await moduleService.updateModule(moduleId, data);
        toast.success('Módulo atualizado com sucesso');
      } else {
        await moduleService.createModule(plan.project_id, {
          ...data,
          projectId: plan.project_id
        });
        toast.success('Módulo criado com sucesso');
      }
      loadData();
    } catch (err) {
      console.error('Erro ao salvar módulo:', err);
      toast.error(err.message || 'Erro ao salvar módulo');
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-16 bg-gray-200 rounded-lg"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="h-40 bg-gray-200 rounded-lg"></div>
            <div className="h-40 bg-gray-200 rounded-lg"></div>
            <div className="h-40 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
          <div>
            <p className="text-red-800 font-medium">{error}</p>
            <button
              onClick={() => navigate('/cutting-plans')}
              className="text-red-600 hover:text-red-800 text-sm mt-1"
            >
              Voltar para lista de planos
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <nav className="flex items-center gap-2 text-sm text-gray-600 mb-4">
        <button
          onClick={() => navigate(`/cutting-plans/${id}`)}
          className="flex items-center gap-1 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Planos de Corte
        </button>
        <span>/</span>
        <span className="text-gray-900 font-medium">Módulos</span>
      </nav>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Módulos do Plano</h1>
          <p className="text-gray-600 mt-1">
            {plan?.plan_code} - {modules.length} módulo(s)
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Novo Módulo
        </button>
      </div>

      {modules.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum módulo encontrado</h3>
          <p className="text-gray-500 mb-4">Comece criando o primeiro módulo para este plano.</p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Criar Módulo
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {modules.map((module) => (
            <ModuleCard
              key={module.id}
              module={module}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onViewParts={handleViewParts}
            />
          ))}
        </div>
      )}

      <ModuleFormModal
        module={editingModule}
        planId={plan?.project_id}
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSave={handleSave}
      />

      <ConfirmModal
        show={confirmState.show}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmState({ show: false, module: null })}
        title="Excluir Módulo"
        message={`Tem certeza que deseja excluir o módulo ${confirmState.module?.module_code}?`}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default ProjectModules;
